import { Suspense, useContext, useEffect, useState } from 'react'
import Section from './_Section'
import createGradientElement from '../../helpers/markup'
import { addBgImageClass, lightOrDark, transformPaddingToTailwind } from '../../helpers/style'
import ButtonEl from '../helpers/ctas/Button'
import { MarkupContext } from '../../providers/Markup'
import { PlayIcon, XMarkIcon } from '@heroicons/react/24/solid'
import Modal from 'react-modal'
import Loader from '../helpers/Loader'

Modal.setAppElement('#root')

async function getYTEmbed(url) {
  try {
    const response = await fetch(`https://www.youtube.com/oembed?url=${url}&format=json`)
    const responseJson = await response.json()
    return responseJson
  } catch (error) {
    console.error(error)
  }
}

const customModalStyle = {
  content: {
    top: '50%',
    left: '50%',
    width: '75vw',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    translate: '-50% -50%',
    aspectRatio: '16/10',
    maxHeight: '95vh',
  },
}

function ContentFiftybyFifty(props) {
  const { createMarkup } = useContext(MarkupContext)
  const [backgroundColor, setBackgroundColor] = useState('transparent')
  const [gradientColor, setGradientColor] = useState('#fff')
  const componentPadding = transformPaddingToTailwind(props.componentPadding)

  // Get content alignment from props
  const contentAlignment = props.contentAlignment?.toLowerCase() || 'left'

  useEffect(() => {
    const bgColor = props.cardBackgroundColour?.replace('#', '') || ''
    const fgColor = props.imageGradientColour?.replace('#', '') || ''
    setBackgroundColor((prevState) => (bgColor ? `#${bgColor}` : prevState))
    setGradientColor((prevState) => (fgColor ? `#${fgColor}` : prevState))
  }, [])

  // Determine text color - default to 'light' (dark text) if background is transparent or not set
  // Note: text-dark = white text (for dark backgrounds), text-light = dark text (for light backgrounds)
  const getTextColor = () => {
    if (!props.backgroundColor && (!backgroundColor || backgroundColor === 'transparent')) {
      return 'light' // Use 'light' class which gives dark text for light/transparent backgrounds
    }
    return lightOrDark(backgroundColor)
  }
  const textColor = getTextColor()

  return (
    <Section type="contentFiftybyFifty" outerClass={componentPadding} {...props}>
      <div className="mx-6">
        <div className="flex flex-col md:grid md:grid-cols-2 gap-2 md:gap-16 mt-12">
          {(props.cardItems || []).map((cardItem, idx) => {
            // Check if card has text content. If yes -> Bottom. If no (Image) -> Top.
            const hasTextContent =
              cardItem.title || cardItem.description || (cardItem.ctaLinkItem && cardItem.ctaLinkItem.length > 0)
            const mobileOrderClass = hasTextContent ? 'order-last' : 'order-first'

            return (
              <CardItem
                key={idx}
                cardItem={cardItem}
                idx={idx}
                backgroundColor={backgroundColor}
                textColor={textColor}
                imageGradientColour={props.imageGradientColour}
                imageGradientType={props.imageGradientType}
                gradientColor={gradientColor}
                createMarkup={createMarkup}
                contentAlignment={contentAlignment}
                className={`w-full ${mobileOrderClass} md:order-none`}
              />
            )
          })}
        </div>
      </div>
    </Section>
  )
}

function CardItem({
  cardItem,
  idx,
  backgroundColor,
  textColor,
  imageGradientColour,
  imageGradientType,
  gradientColor,
  createMarkup,
  contentAlignment,
  className,
}) {
  const [modalIsOpen, setIsOpen] = useState(false)
  const [videoEmbed, setVideoEmbed] = useState(null)

  // Define separate images for Mobile (Static) and Desktop (Background)
  const visualImage = cardItem.mobileImage || cardItem.desktopImage
  const desktopBgImage = cardItem.desktopImage || cardItem.mobileImage

  function openModal() {
    setIsOpen(true)
  }

  async function afterOpenModal() {
    if (cardItem.videoUrl) {
      const embedCode = await getYTEmbed(cardItem.videoUrl)
      if (embedCode && !embedCode.error) {
        setVideoEmbed(embedCode)
      }
    }
  }

  function closeModal() {
    setIsOpen(false)
  }

  // Alignment for all properties except title: uses contentAlignment
  const alignmentClass =
    contentAlignment === 'center' ? 'items-center' : contentAlignment === 'right' ? 'items-end' : 'items-start'
  const contentTextAlignment =
    contentAlignment === 'center' ? 'text-center' : contentAlignment === 'right' ? 'text-right' : 'text-left'

  // Title: uses titleAlignment if set, otherwise falls back to contentAlignment
  const titleAlignmentValue = cardItem.titleAlignment?.toLowerCase() || contentAlignment
  const titleTextAlignment =
    titleAlignmentValue === 'center' ? 'text-center' : titleAlignmentValue === 'right' ? 'text-right' : 'text-left'

  return (
    <>
      <div
        style={{
          '--bg-color': backgroundColor,
          '--light-or-dark': textColor,
          backgroundColor: backgroundColor !== 'transparent' ? backgroundColor : undefined,
        }}
        className={`${className} relative overflow-hidden flex flex-col lg:block ${textColor === 'light' ? 'text-dark' : 'text-light'} lg:basis-1/2 lg:relative lg:min-h-[var(--banner-min-height,620px)]`}
      >
        {/* Mobile Image (Visible < lg) - Renders at top of stack */}
        {visualImage && (
          <div
            className="w-full h-64 bg-cover bg-center shrink-0 lg:hidden"
            style={{ backgroundImage: `url(${visualImage.src})` }}
            role="img"
            aria-label={visualImage.alt || 'Section image'}
          />
        )}

        {/* Desktop Background (Visible >= lg) - Absolute behind text */}
        {desktopBgImage && (
          <div
            className="hidden lg:block absolute inset-0 w-full h-full bg-cover bg-center z-0"
            style={{ backgroundImage: `url(${desktopBgImage.src})` }}
            role="img"
            aria-label={desktopBgImage.alt || ''}
          />
        )}

        {imageGradientColour &&
          createGradientElement({
            type: imageGradientType,
            fromColor: gradientColor,
            classes: 'rounded-2xl',
          })}
        <div
          className={`pt-1 lg:p-8 lg:pb-10 lg:min-h-72 relative z-10 flex flex-col lg:h-full lg:justify-center lg:items-center lg:self-stretch ${cardItem.videoUrl ? 'justify-between' : 'justify-start'}`}
        >
          {cardItem.videoUrl && (
            <ButtonEl className="h-20 w-20 mx-auto mt-20" onClick={openModal} theme="none">
              <PlayIcon role="none" className="text-current" />
              <span className="sr-only">Play video</span>
            </ButtonEl>
          )}
          <div className="flex flex-col">
            {cardItem.title && (
              <div
                className={`font-highlight max-w-[var(--readable-content-max-width,none)] ${titleTextAlignment}`}
                data-rte="true"
                dangerouslySetInnerHTML={createMarkup(cardItem.title)}
              ></div>
            )}
            {cardItem.description && (
              <div
                className={`mt-2 ${contentTextAlignment}`}
                data-rte="true"
                dangerouslySetInnerHTML={createMarkup(cardItem.description)}
              />
            )}

            {cardItem.ctaLinkItem && cardItem.ctaLinkItem.length != 0 && (
              <div className={`flex flex-col ${alignmentClass} gap-6 mt-3 lg:mt-5`}>
                {cardItem.ctaLinkItem.map((ctaLinkItem, i) => (
                  <ButtonEl
                    key={i}
                    link={ctaLinkItem.link}
                    theme={ctaLinkItem.linkType.toLowerCase()}
                    icon={ctaLinkItem.linkIconClass}
                    position={ctaLinkItem.linkPosition}
                    className="max-md:w-full max-md:justify-center"
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      {cardItem.videoUrl && (
        <Modal
          isOpen={modalIsOpen}
          onAfterOpen={afterOpenModal}
          onRequestClose={closeModal}
          style={customModalStyle}
          contentLabel="Video Modal"
          aria={{
            labelledby: `modal-heading-${idx}`,
          }}
        >
          <ButtonEl className="right-5 absolute" onClick={closeModal} theme="none">
            <XMarkIcon role="none" className="w-6 h-6 shrink text-current" />
            <span className="sr-only">Close video modal</span>
          </ButtonEl>
          <div data-type="video-modal-content" className="m-0 relative w-full">
            {videoEmbed && (
              <div id={`modal-heading-${idx}`} className="absolute -top-[1000px] opacity-0">
                {videoEmbed.title}
              </div>
            )}
            <Suspense fallback={<Loader />}>
              {videoEmbed && (
                <div
                  data-type="video-iframe-wrapper"
                  className="w-full h-full"
                  data-rte="true"
                  dangerouslySetInnerHTML={createMarkup(videoEmbed.html)}
                />
              )}
            </Suspense>
          </div>
        </Modal>
      )}
    </>
  )
}

export default ContentFiftybyFifty
